/* oxlint-disable @typescript-eslint/no-use-before-define */
/* oxlint-disable react/jsx-no-constructed-context-values */

'use client';

import { RecipeVariantProps } from '@pandacss/types';
import * as m from 'motion/react-m';
import { ReactNode, createContext, useContext, useEffect } from 'react';

import { Styles, css, cx, sva } from '@flipster/styled-system/css';
import { token } from '@flipster/styled-system/tokens';

import XMarkIcon from '../icon/generated/XMarkIcon';
import { lockScroll } from '../misc/browser/scroll';
import { Portal } from '../misc/portal';

interface Props {
  type?: ModalVariantProps['type'];
  size?: ModalVariantProps['size'];
  children: ReactNode;
  scrollable?: boolean;
  css?: Styles;
}

const initial = { opacity: 0.5 };
const animate = { opacity: 1, transition: { ease: 'easeOut' as const, duration: 0.2 } };
const exit = { opacity: 0.5, transition: { ease: 'easeIn' as const, duration: 0.2 } };

const DialogContext = createContext<{ slots?: ReturnType<typeof modalSlotRecipe.raw> }>({});

const ModalBase = ({ type = 'normal', size = 'small', scrollable, css: cssProps, children }: Props) => {
  useEffect(() => {
    const cleanUp = lockScroll();
    return () => {
      cleanUp();
    };
  }, []);

  const slots = modalSlotRecipe.raw({ type, size, scrollable });
  return (
    <Portal selector='#modal'>
      <DialogContext value={{ slots }}>
        <m.aside className={css(slots.root)} initial={initial} animate={animate} exit={exit}>
          <m.section
            className={css(slots.dialog, cssProps)}
            initial={{ ...initial, scale: 0.8 }}
            animate={{ ...animate, scale: 1 }}
            exit={{ ...exit, scale: 0.8 }}
          >
            {children}
          </m.section>
        </m.aside>
      </DialogContext>
    </Portal>
  );
};

const Close = ({ onClose }: { onClose: () => void }) => {
  const { slots } = useContext(DialogContext);
  return (
    <div className={css(slots?.close)}>
      <button type='button' onClick={onClose} className={css({ mt: 16, mx: 16 })}>
        <XMarkIcon size={24} color={token('colors.content.default.level-4')} />
      </button>
    </div>
  );
};

interface BaseProps {
  css?: Styles;
  children: ReactNode;
}

const Header = ({ css: cssProps, children }: BaseProps) => {
  const { slots } = useContext(DialogContext);
  return <header className={css(slots?.header, cssProps)}>{children}</header>;
};

interface TitleProps extends Omit<BaseProps, 'children'> {
  label: ReactNode;
}

const Title = ({ css: cssProps, label }: TitleProps) => {
  const { slots } = useContext(DialogContext);
  return <h2 className={css(slots?.title, cssProps)}>{label}</h2>;
};

const Content = ({ css: cssProps, children }: BaseProps) => {
  const { slots } = useContext(DialogContext);
  return <div className={cx('dialog__content', css(slots?.content, cssProps))}>{children}</div>;
};

const Footer = ({ css: cssProps, children }: BaseProps) => {
  const { slots } = useContext(DialogContext);
  return <footer className={css(slots?.footer, cssProps)}>{children}</footer>;
};

export const Modal = Object.assign(ModalBase, {
  Close,
  Header,
  Title,
  Content,
  Footer,
});

export const modalSlotRecipe = sva({
  className: 'modal',
  slots: ['root', 'dialog', 'close', 'header', 'title', 'content', 'footer'],
  base: {
    root: {
      zIndex: 'modal',
      pos: 'fixed',
      top: 0,
      left: 0,
      w: '100dvw',
      h: '100dvh',
      maxH: '100dvh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      bgColor: 'background.interaction.dim',
    },
    dialog: {
      pos: 'relative',
      display: 'flex',
      flexDirection: 'column',
      h: { tabletDown: '100dvh!' },
      maxH: { tablet: 'calc(100% - 60px)' },
      rounded: { tablet: 8 },
      bgColor: 'background.default.level-1',
      wordBreak: 'break-word',
      overflow: 'hidden',
    },
    close: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    header: {
      px: { base: 16, tablet: 32 },
      _first: { pt: 24 },
    },
    title: {
      textStyle: 'Title/M',
      color: 'content.default.level-4',
      py: 8,
    },
    content: {
      flex: 1,
      textStyle: 'Text/L/Regular',
      color: 'content.default.level-3',
      overflowY: 'auto',
    },
    footer: {
      display: 'flex',
      justifyContent: 'center',
      w: '100%',
      gap: 8,
      px: 16,
      pt: 16,
      pb: 24,
      '& > button': { tabletDown: { flex: 1 }, tablet: { minW: 180 } },
    },
  },
  variants: {
    type: {
      normal: {
        header: {
          px: { base: 16, tablet: 32 },
        },
        content: {
          pt: 24,
          px: { base: 16, tablet: 32 },
          pb: { base: 16, tablet: 32 },
        },
      },
      image: {
        close: {
          position: 'absolute',
          top: 0,
          right: 0,
        },
        header: {
          px: 20,
          pt: 32,
          pb: 6,
        },
        content: {
          px: 20,
          pt: 6,
          pb: { base: 16, tablet: 32 },
        },
      },
    },
    size: {
      small: {
        dialog: {
          w: { base: '100vw', tablet: 480 },
        },
      },
      large: {
        dialog: {
          w: { base: '100vw', tablet: 800 },
        },
      },
    },
    scrollable: {
      true: {
        root: {
          boxShadowColor: 'background.default.level-1',
          '&:has(footer) .dialog__content': {
            mb: 88,
          },
          '&:has(footer) footer': {
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
          },
        },
        header: {
          zIndex: 1,
          boxShadow: '0 18px 16px -8px var(--shadow-color)',
        },
        content: {
          zIndex: 0,
        },
        footer: {
          zIndex: 1,
          boxShadow: '0 -18px 16px -8px var(--shadow-color)',
        },
      },
    },
  },
});

type ModalVariantProps = Exclude<RecipeVariantProps<typeof modalSlotRecipe>, undefined>;
